/**
 * UserMenu -- Auth-aware user menu for header bars.
 *
 * When logged out: shows a plain User icon button.
 * When logged in: shows user name/email with a dropdown containing
 * user info and a Sign Out button.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { User, LogOut, ChevronDown, Loader2, Settings, Shield } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { useAuth } from '@/lib/auth'

/**
 * Dropdown anchoring. The website HeaderBar sits at the top-right of the
 * screen so its menu opens DOWN-RIGHT (the default). The plugin ActivityBar
 * bottom cluster sits at the bottom-left, where down-right would open
 * off-screen — it passes 'up-right' so the menu anchors to the button and
 * opens UP and to the RIGHT, into the content area.
 */
export type MenuPlacement = 'down-right' | 'up-right'

const PLACEMENT_CLASSES: Record<MenuPlacement, string> = {
  'down-right': 'right-0 top-full mt-1',
  'up-right': 'left-full bottom-0 ml-1',
}

interface UserMenuProps {
  placement?: MenuPlacement
  /**
   * 'full' (default) shows name/email + chevron — fits the wide website
   * HeaderBar. 'avatar' shows a compact initial-circle — for the narrow plugin
   * ActivityBar (56px), where the name text overflows the strip. The dropdown
   * (with full name/email) is identical either way.
   */
  variant?: 'full' | 'avatar'
}

export function UserMenu({
  placement = 'down-right',
  variant = 'full',
}: UserMenuProps) {
  const { isLoggedIn, user, logout, isLoggingOut } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // This menu is shared between the website HeaderBar and the plugin's embed
  // ActivityBar. The account page lives at a different route per surface:
  // /embed/main/account inside the editor vs /account on the web. A bare
  // "/account" in the embed bundle hits the catch-all and bounces to the
  // dashboard, so route by context.
  const { pathname } = useLocation()
  const accountPath = pathname.startsWith('/embed/')
    ? '/embed/main/account'
    : '/account'

  const closeMenu = useCallback(() => setIsOpen(false), [])

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return

    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, closeMenu])

  // Not logged in: render nothing — AuthCard on dashboard handles login
  if (!isLoggedIn) {
    return null
  }

  // Logged in: name/email + dropdown
  const displayName = user?.name || user?.email || 'User'
  const initial = displayName.trim().charAt(0).toUpperCase() || 'U'

  return (
    <div ref={menuRef} className="relative">
      {variant === 'avatar' ? (
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          title={displayName}
          aria-label={displayName}
          className="flex size-8 items-center justify-center rounded-full border border-ue-border-medium bg-ue-bg-hover text-xs font-semibold text-ue-text-primary transition-colors cursor-pointer hover:border-ue-accent-primary"
        >
          {initial}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center gap-1 px-2 h-8 rounded hover:bg-ue-bg-hover transition-colors cursor-pointer"
        >
          <span className="text-xs text-ue-text-secondary hover:text-ue-text-primary transition-colors truncate max-w-[120px]">
            {displayName}
          </span>
          <ChevronDown className="size-3 text-ue-text-tertiary shrink-0" />
        </button>
      )}

      {isOpen && (
        <div
          className={cn(
            'absolute w-48 rounded border border-ue-border-medium bg-ue-bg-dark shadow-lg z-50 py-1',
            PLACEMENT_CLASSES[placement],
          )}
        >
          {/* User info header */}
          <div className="px-3 py-2 border-b border-ue-border-medium">
            {user?.name && (
              <p className="text-xs font-medium text-ue-text-primary truncate">
                {user.name}
              </p>
            )}
            {user?.email && (
              <p className="text-[11px] text-ue-text-tertiary truncate">
                {user.email}
              </p>
            )}
          </div>

          {/* My Account */}
          <Link
            to={accountPath}
            onClick={closeMenu}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-ue-text-secondary hover:bg-ue-bg-hover hover:text-ue-text-primary transition-colors"
          >
            <Settings className="size-3.5" />
            My Account
          </Link>

          {/* Admin Panel (admin only) */}
          {user?.role === 'admin' && (
            <Link
              to="/admin/users"
              onClick={closeMenu}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-ue-text-secondary hover:bg-ue-bg-hover hover:text-ue-text-primary transition-colors"
            >
              <Shield className="size-3.5" />
              Admin Panel
            </Link>
          )}

          {/* Sign Out */}
          <button
            type="button"
            onClick={() => {
              logout()
              closeMenu()
            }}
            disabled={isLoggingOut}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-ue-text-secondary hover:bg-ue-bg-hover hover:text-ue-text-primary transition-colors disabled:opacity-50"
          >
            {isLoggingOut ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <LogOut className="size-3.5" />
            )}
            Sign Out
          </button>
        </div>
      )}
    </div>
  )
}
